/**
 * Encodage et décodage des trames du canal de données.
 *
 * Ce module fait le lien entre trois préoccupations qui ne doivent pas se mélanger :
 * le **format binaire** (défini dans le package partagé), le **chiffrement** (crypto.ts)
 * et le **protocole de contrôle** (types Zod partagés).
 *
 * Le regroupement ici évite l'erreur la plus facile à commettre dans ce genre de code :
 * chiffrer sans lier la trame à sa position, ce qui ouvrirait la porte au rejeu.
 */

import {
  FRAME_TYPE_CHUNK,
  FRAME_TYPE_CONTROL,
  decodeDcControl,
  decodeFrame,
  encodeFrame,
  frameAad,
  type DcControl,
  type Bytes,
  type Frame,
} from '@attrape/shared';
import { ivForSeq, openBytes, sealBytes } from './crypto.js';

/**
 * Index de fichier réservé aux trames de contrôle.
 *
 * Une valeur sentinelle plutôt qu'un index ordinaire : même si une session comptait
 * 4 milliards de fichiers, un message de contrôle ne pourrait pas se confondre avec
 * le fichier de même index.
 */
const CONTROL_FILE_INDEX = 0xffffffff;

export type DecodedFrame =
  { kind: 'control'; control: DcControl } | { kind: 'chunk'; fileIndex: number; data: Bytes };

export interface DecodedEnvelope {
  seq: number;
  decoded: DecodedFrame;
}

export class FrameCodec {
  private readonly encoder = new TextEncoder();
  private readonly decoder = new TextDecoder();

  constructor(private readonly key: CryptoKey) {}

  /** Encode un message de contrôle scellé. */
  async encodeControl(control: DcControl, seq: number): Promise<Bytes> {
    const plaintext = this.encoder.encode(JSON.stringify(control));
    return this.seal(FRAME_TYPE_CONTROL, CONTROL_FILE_INDEX, seq, plaintext);
  }

  /** Encode un morceau de fichier scellé. */
  async encodeChunk(fileIndex: number, seq: number, plaintext: Bytes): Promise<Bytes> {
    return this.seal(FRAME_TYPE_CHUNK, fileIndex, seq, plaintext);
  }

  /**
   * Décode et déchiffre une trame brute.
   *
   * Lève si le déchiffrement échoue ou si un message de contrôle est malformé. Une
   * exception ici signifie une donnée non authentifiée : elle ne doit jamais être
   * transformée en valeur par défaut silencieuse.
   */
  async decode(raw: ArrayBuffer | Bytes): Promise<DecodedEnvelope> {
    const frame = decodeFrame(raw);
    const plaintext = await openBytes(
      this.key,
      frame.iv,
      frame.payload,
      frameAad(frame.type, frame.fileIndex, frame.seq),
    );

    if (frame.type === FRAME_TYPE_CHUNK) {
      return {
        seq: frame.seq,
        decoded: { kind: 'chunk', fileIndex: frame.fileIndex, data: plaintext },
      };
    }

    if (frame.type === FRAME_TYPE_CONTROL) {
      const parsed = decodeDcControl(this.decoder.decode(plaintext));
      if (!parsed.ok) {
        throw new Error(`Message de contrôle invalide : ${parsed.error}`);
      }
      return { seq: frame.seq, decoded: { kind: 'control', control: parsed.value } };
    }

    throw new Error(`Type de trame inconnu : ${frame.type}`);
  }

  private async seal(
    type: number,
    fileIndex: number,
    seq: number,
    plaintext: Bytes,
  ): Promise<Bytes> {
    const iv = ivForSeq(seq);
    const payload = await sealBytes(this.key, plaintext, iv, frameAad(type, fileIndex, seq));
    const frame: Frame = { type, fileIndex, seq, iv, payload };
    return encodeFrame(frame);
  }
}
